import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../../Apis/apiWrapper";
import { crmEndpoints } from "../../../utils/constants/httpConstants";
import EmailLogList from "./components/EmailLogList";
import ListingTaskCard from "./components/ListingTaskCard";
import { STRINGS } from "../strings";
import classes from "./Crm.module.css";
import { TIMELINE_CRM } from "../../../utils/constants/assets";
import ContactDetailEmptyScreen from "./components/ContactDetailEmptyScreen";
import SkeletonLoaderCRMScreens from "./components/SkeletonLoaderCRMScreens";
import EmailSentList from "./components/EmailSentList";
import ListingNoteCard from "./components/ListingNoteCard";
import ListingMeetingCard from "./components/ListingMeetingCard";
import TimelineRow from "./components/TimelineRow";
import timelineStyles from "./components/timelineRow.module.css";
import { ICON_LABELS, fontWeight } from "../../../utils/constants/UI";
import IconComponent from "../../../components/Icon/IconComponent";
import parse from "html-react-parser";
import { Button } from "../../../components/Buttons";
import CustomModal from "../../../components/Modals/CustomModal";
import TypographyInline from "../../../components/Typography/TypographyInline";
import EventEmitter from "../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../utils/constants";
import { decompressString } from "../../../utils/helpers";
import { decompressAndFormat } from "./utils/formatNotesContent";
import {
  WARNING,
  HELP,
  PURPLE,
  GREY,
  BORDER,
  SUCCESS,
} from "../../../utils/constants/colors";

const decompressNotes = (val) => {
  try {
    return decodeURIComponent(decompressString(val));
  } catch (e) {
    return decompressString(val);
  }
};

// Campaign-driven tasks (from_campaign === true) store the description in
// `body` while manual user tasks store it in `notes`. ListingTaskCard reads
// `notes`, so we surface whichever field actually has content, decompressed
// when `is_compressed` is set. This is purely additive - manual tasks with
// real `notes` still flow through the existing decompressNotes path.

const escapeHtmlForTask = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const formatPlainTextTaskBody = (input) => {
  if (typeof input !== "string") return input;
  if (/<[a-zA-Z][^>]*>/.test(input)) return input;
  const text = input.replace(/\r\n/g, "\n").trim();
  if (!text) return "";
  const blocks = text.split(/\n{2,}/);
  const renderBlock = (block) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return "";
    const isBullet = (l) => /^[•\-*]\s+/.test(l);
    if (lines.every(isBullet)) {
      return `<ul>${lines
        .map(
          (l) =>
            `<li>${escapeHtmlForTask(l.replace(/^[•\-*]\s+/, ""))}</li>`,
        )
        .join("")}</ul>`;
    }
    const parts = [];
    let bullets = [];
    const flush = () => {
      if (bullets.length) {
        parts.push(
          `<ul>${bullets
            .map((b) => `<li>${escapeHtmlForTask(b)}</li>`)
            .join("")}</ul>`,
        );
        bullets = [];
      }
    };
    lines.forEach((l) => {
      if (isBullet(l)) {
        bullets.push(l.replace(/^[•\-*]\s+/, ""));
        return;
      }
      flush();
      const m = l.match(/^([A-Z][A-Za-z ]{1,40}):\s*(.*)$/);
      if (m) {
        parts.push(
          m[2]
            ? `<p><strong>${escapeHtmlForTask(m[1])}:</strong> ${escapeHtmlForTask(m[2])}</p>`
            : `<p><strong>${escapeHtmlForTask(m[1])}:</strong></p>`,
        );
      } else {
        parts.push(`<p>${escapeHtmlForTask(l)}</p>`);
      }
    });
    flush();
    return parts.join("");
  };
  return blocks.map(renderBlock).join("");
};

const pickTaskContent = (prop) => {
  if (!prop) return prop;
  const hasNotes =
    typeof prop.notes === "string" && prop.notes.trim().length > 0;
  const hasBody =
    typeof prop.body === "string" && prop.body.trim().length > 0;
  if (!hasNotes && !hasBody) return prop;
  let raw;
  if (hasNotes) {
    raw = prop.is_compressed ? decompressNotes(prop.notes) : prop.notes;
  } else {
    raw = prop.is_compressed ? decompressNotes(prop.body) : prop.body;
  }
  return { ...prop, notes: formatPlainTextTaskBody(raw) };
};

const adaptCampaignEmailSent = (prop, contactEmail, overrides = {}) => {
  const p = prop?.payload || {};
  return {
    ...prop,
    type: "sent",
    subject: p.subject,
    body: p.body,
    is_compressed: p.is_compressed,
    from_email: prop?.email || contactEmail || "",
    to_emails: contactEmail ? [contactEmail] : prop?.email ? [prop.email] : [],
    cc_emails: [],
    bcc_emails: [],
    attachment_links: [],
    pin: 0,
    thread_id: null,
    status: "sent",
    created_at: prop?.created_at,
    ...overrides,
  };
};

// Keys are lower-cased before lookup. The campaign queue's own vocabulary
// (active / queued) lands here too now that the sync worker copies the live step
// status onto the activity row — without these two the chip fell through to the
// "Scheduled" default, which is what made a PAUSED campaign keep reading Scheduled.
const SCHEDULED_STATUS_LABELS = {
  pause: "Paused",
  paused: "Paused",
  scheduled: "Scheduled",
  active: "Scheduled",
  queued: "Scheduled",
  sent: "Sent",
  failed: "Failed",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  unenrolled: "Unenrolled",
  stopped: "Stopped",
  // Campaign row parked because the contact was enrolled in a sales sequence.
  suppressed: "Cancelled",
};

// Per backend contract: step_type 1 = email, 2 = task, 3 = call. Used by
// both `email_pending` and `email_scheduled` (despite the names, those
// records can carry call/task steps too - the activity_type prefix is a
// backend artifact, not a content guarantee).
const STEP_TYPE_LABELS = {
  1: "Email",
  2: "Task",
  3: "Call",
};

// Map step_type -> the matching ICON_THEMES entry so the icon/colour match
// the actual touch kind (email orange / task yellow / call green) instead
// of always rendering email.
const stepThemeKey = (stepType) => {
  const n = Number(stepType);
  if (n === 2) return "task";
  if (n === 3) return "call";
  return "email";
};

// Adapter: campaign payload -> the shape ListingTaskCard expects. Used for
// non-email campaign steps (calls / tasks) so the timeline detail uses the
// same rich card as the standalone Calls / Tasks tabs - title strip,
// priority / due date / time / reminder row, formatted description.
const adaptCampaignStepToTaskCard = (prop) => {
  const p = prop?.payload || {};
  return {
    _id: prop?._id,
    title: p.subject || "",
    // The shared formatter (used by notes/calls/tasks) decompresses + lays
    // out headings/bullets correctly. ListingTaskCard reads `notes` and
    // renders it via parse(), matching the standalone tab.
    notes: decompressAndFormat(p.body, p.is_compressed) || "",
    is_compressed: false,
    step_type: Number(p.step_type) || undefined,
    status: false,
    pin: false,
    priority: null,
    send_reminder: 0,
    show_time_stamp: p.scheduled_at || prop?.created_at_ms || prop?.created_at,
    from_campaign: true,
    // Pre-rendered title shown inside the card body. Matches the same field
    // ListingTaskCard reads when expanding from the standalone tab.
    subject: p.subject || "",
  };
};

// Pick the right detail renderer for a non-email campaign step.
//   step_type 3 -> ListingTaskCard in "call" mode (green icon, call layout)
//   step_type 2 -> ListingTaskCard in "todo" mode (yellow icon, task layout)
// `hideHeader` keeps the rich card body (title strip + priority/due/time/
// reminder + description) without re-rendering the "Call logged by ..." +
// clock-icon header, which the parent timeline row already owns.
// Returns null when there's no body to show so the expander is skipped.
const renderStepDetailCard = (prop, contactDetails, contactId) => {
  const p = prop?.payload || {};
  if (!p.body && !p.subject) return null;
  const cardType = Number(p.step_type) === 3 ? "call" : "todo";
  return (
    <ListingTaskCard
      contactDetails={contactDetails}
      logData={adaptCampaignStepToTaskCard(prop)}
      contact_id={contactId}
      type={cardType}
      showOperation={false}
      hideHeader
    />
  );
};

// Sales-sequence variant of the adapter above. The sales-sequence payload
// uses `title` (plain string) + `notes` (plain text, may contain
// "Heading:" sections and "- " bullets) instead of the campaign payload's
// `subject` + compressed `body`. Route the notes through the shared
// formatter so headings / bullets render the same way as standalone notes.
const adaptSalesSequenceStepToTaskCard = (prop) => {
  const p = prop?.payload || {};
  return {
    _id: prop?._id,
    title: p.title || "",
    // formatPlainTextToHtml via decompressAndFormat with is_compressed=false
    // - just runs the heading/bullet HTML pass without trying to inflate.
    notes: decompressAndFormat(p.notes, false) || "",
    is_compressed: false,
    step_type: Number(p.step_type) || undefined,
    status: false,
    pin: false,
    priority: null,
    send_reminder: 0,
    show_time_stamp:
      p.scheduled_at || prop?.created_at_ms || prop?.created_at,
    from_campaign: true,
    subject: p.title || "",
  };
};

// Sales-sequence call_scheduled / task_scheduled rows. Mirrors
// renderStepDetailCard but reads the sales-sequence payload shape.
//   activity_type "call_scheduled" -> "call" (green icon).
//   activity_type "task_scheduled" -> "todo" (yellow icon).
// step_type on the payload is not reliable across sales-sequence rows
// (sometimes 2 or 3 for a "call" activity_type), so we drive the visual
// kind off activity_type instead.
const renderSalesSequenceDetailCard = (
  prop,
  contactDetails,
  contactId,
  kind, // "call" | "todo"
) => {
  const p = prop?.payload || {};
  if (!p.notes && !p.title) return null;
  return (
    <ListingTaskCard
      contactDetails={contactDetails}
      logData={adaptSalesSequenceStepToTaskCard(prop)}
      contact_id={contactId}
      type={kind}
      showOperation={false}
      hideHeader
    />
  );
};

// Matches the 5-icon palette in the contact header (IconOutlinedHover).
// Each timeline icon uses the same bg + icon color as its header counterpart
// so the visual language is consistent across the screen.
//   Email   -> orange  bg #FEECDC / icon #FF8A4C
//   Call    -> green   bg #E8F6ED / icon #01A63E
//   Task    -> yellow  bg #FFF5E5 / icon #F4A118
//   Note    -> blue    bg #EBF1FD / icon #2080F6
//   Meeting -> purple  bg #EEECFF / icon #9162FA
// Campaign stays green (uses the same palette as Call since they're both
// activity rollups; tweak later if a distinct accent is wanted).
const ICON_THEMES = {
  email:     { icon: ICON_LABELS.EMAIL, color: "#FF8A4C", bg: "#FEECDC", border: "#FEECDC" },
  emailOpen: { icon: ICON_LABELS.VISIBILITY_ON, color: "#F4A118", bg: "#FFF5E5", border: "#FFF5E5" },
  call:      { icon: ICON_LABELS.CALL, color: "#01A63E", bg: "#E8F6ED", border: "#E8F6ED" },
  meeting:   { icon: ICON_LABELS.EVENT, color: "#9162FA", bg: "#EEECFF", border: "#EEECFF" },
  note:      { icon: ICON_LABELS.EDIT_NOTE || ICON_LABELS.DESCRIPTION, color: "#2080F6", bg: "#EBF1FD", border: "#EBF1FD" },
  task:      { icon: ICON_LABELS.TASK, color: "#F4A118", bg: "#FFF5E5", border: "#FFF5E5" },
  campaign:  { icon: ICON_LABELS.CAMPAIGN, color: "#01A63E", bg: "#E8F6ED", border: "#E8F6ED" },
  attachment:{ icon: ICON_LABELS.ATTACH_FILE, color: "#5F5F61", bg: "#F1F1F2", border: "#E5E5E6" },
};

const STATE_CHIP = {
  completed: { bg: SUCCESS.W_50, color: SUCCESS.W_700, border: SUCCESS.W_100 },
  active: { bg: HELP.W_50, color: HELP.W_700, border: HELP.W_100 },
  paused: { bg: WARNING.W_50, color: WARNING.W_700, border: WARNING.W_100 },
  scheduled: { bg: PURPLE.W_50, color: PURPLE.W_500, border: PURPLE.W_100 },
  "ready to run": { bg: SUCCESS.W_50, color: SUCCESS.W_500, border: SUCCESS.W_100 },
  unenrolled: { bg: BORDER.DISABLED, color: GREY.SECONDARY, border: BORDER.DEFAULT },
  stopped: { bg: BORDER.DISABLED, color: GREY.SECONDARY, border: BORDER.DEFAULT },
  // A step the backend has resolved as over: cancelled reads spent like stopped,
  // sent reads done, failed borrows the warning tone paused already uses.
  cancelled: { bg: BORDER.DISABLED, color: GREY.SECONDARY, border: BORDER.DEFAULT },
  // A campaign whose doc is gone will never run again; read it as spent, like stopped.
  deleted: { bg: BORDER.DISABLED, color: GREY.SECONDARY, border: BORDER.DEFAULT },
  sent: { bg: SUCCESS.W_50, color: SUCCESS.W_700, border: SUCCESS.W_100 },
  failed: { bg: WARNING.W_50, color: WARNING.W_700, border: WARNING.W_100 },
  default: { bg: BORDER.DISABLED, color: GREY.SECONDARY, border: BORDER.DEFAULT },
};

const toTitleCase = (input) => {
  if (!input || typeof input !== "string") return "";
  return input
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// Render the backend `activity_title` ("Email scheduled by Arpit Singh") so
// the action verb stays bold while the actor suffix de-emphasises. Keeps the
// scanability of the original timeline labels but uses the server's
// localized, actor-aware copy when available. JSX is returned because
// TimelineRow's title slot accepts any node.
const formatActivityTitle = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  // Split on the first " by " (case-insensitive) so "Email scheduled by X"
  // becomes "Email scheduled" + " by X". Use lookahead-style split via
  // index so we keep the verb crisp.
  const match = raw.match(/^(.*?)(\s+by\s+.+)$/i);
  if (!match) {
    return <strong>{raw}</strong>;
  }
  const [, verb, rest] = match;
  return (
    <>
      <strong>{verb}</strong>
      <span style={{ fontWeight: 400, color: "#5F5F61" }}>{rest}</span>
    </>
  );
};

// Use the backend's activity_title (rendered with split styling) when
// present; otherwise fall back to the locally-built label string.
const pickTitle = (prop, fallback) => {
  const fromApi = formatActivityTitle(prop?.activity_title);
  return fromApi || fallback;
};

// Prefix a "Step N: " marker to any per-step row (pending, scheduled,
// unenrolled, call_scheduled, task_scheduled) so the user can read the
// sequence position at a glance, regardless of campaign state. No-op when
// step_number isn't on the payload.
const withStepPrefix = (titleNode, stepNumber) => {
  if (!stepNumber && stepNumber !== 0) return titleNode;
  return (
    <>
      <strong>{`Step ${stepNumber}: `}</strong>
      {titleNode}
    </>
  );
};

const buildRow = ({ prop, contactDetails, contactId }) => {
  const date = prop?.created_at_ms || prop?.created_at;

  switch (prop?.activityType) {
    case "log":
      // Right column reflects the user-chosen contact date
      // (processing_date_time) rather than the record's created_at - that
      // way "Due 28 May" lines up with the inner Due Date field instead of
      // showing the log-creation timestamp.
      return {
        ...ICON_THEMES.email,
        title: pickTitle(prop, "Email logged"),
        subtitle: prop?.subject,
        date: prop?.processing_date_time || date,
        detail: (
          <EmailLogList
            contactDetails={contactDetails}
            logData={prop}
            showOperation={false}
          />
        ),
      };

    case "mail":
      return {
        ...ICON_THEMES.email,
        title: pickTitle(prop, "Email sent"),
        subtitle: prop?.subject,
        // Same rule the "log" case already uses: show when the mail is due to go
        // out (or went out), not when the row was written. `created_at` is the
        // moment Compose was submitted, so a mail scheduled for next week printed
        // today's timestamp. This also matches how the row is SORTED — both the
        // backend (getSortMs) and sortTimestamp below key off processing_date_time,
        // so the displayed date disagreed with its own position in the feed.
        date: prop?.processing_date_time || date,
        detail: (
          <EmailSentList
            contactDetails={contactDetails}
            emailData={prop}
            showOperation={false}
          />
        ),
      };

    case "email_sent": {
      const p = prop?.payload || {};
      return {
        ...ICON_THEMES.email,
        title: withStepPrefix(
          pickTitle(prop, "Email Sent"),
          p.step_number,
        ),
        subtitle: p.subject ? `Subject: ${p.subject}` : undefined,
        // Real send time (same precedence the email_scheduled case uses). `date` is only
        // the snapshot/created_at, which is when the contact was added to CRM.
        date: p.show_date_time || p.sent_at || date,
        detail: (
          <EmailSentList
            contactDetails={contactDetails}
            emailData={adaptCampaignEmailSent(prop, contactDetails?.email)}
            showOperation={false}
          />
        ),
      };
    }

    case "pending":
    case "email_pending": {
      // Emitted while a campaign is in "Ready To Run" - the touch has been
      // generated but not yet scheduled. The backend's `activity_title`
      // already disambiguates the kind ("Email Schedule Pending",
      // "Call Schedule Pending", "Task Schedule Pending"), so we trust it
      // for the title and only use step_type to pick the icon/theme and
      // pick the right body-preview renderer. We prefix the title with
      // "Step N" so users can read the sequence position at a glance.
      const p = prop?.payload || {};
      const themeKey = stepThemeKey(p.step_type);
      const isEmailStep = themeKey === "email";
      const chipTheme = STATE_CHIP.scheduled;
      const baseTitle = pickTitle(prop, prop?.activity_title || "Pending");
      const titleWithStep = withStepPrefix(baseTitle, p.step_number);
      // Email steps get the full EmailSentList preview. Calls/tasks use
      // ListingTaskCard so the timeline detail matches the rich card used
      // on the standalone Calls / Tasks tabs.
      const detail = isEmailStep ? (
        <EmailSentList
          contactDetails={contactDetails}
          emailData={adaptCampaignEmailSent(prop, contactDetails?.email, {
            status: "pending",
          })}
          showOperation={false}
        />
      ) : (
        renderStepDetailCard(prop, contactDetails, contactId)
      );
      // Pending rows don't have a real send/run time yet - the campaign
      // hasn't been scheduled, so leaving `created_at` in the right column
      // would imply a date that isn't meaningful. Omit the date entirely
      // until the row transitions to email_scheduled / email_sent.
      // The backend re-reads a step's status at request time, so a pending touch whose
      // campaign has since been deleted or finished arrives with `step_status_resolved`
      // set. Without this it kept reading "Pending" forever - work that will never run.
      const resolved = (prop?.step_status_resolved || "").toString().toLowerCase();
      const pendingChipLabel = resolved
        ? SCHEDULED_STATUS_LABELS[resolved] || "Pending"
        : "Pending";
      return {
        ...ICON_THEMES[themeKey],
        title: titleWithStep,
        chip: {
          label: pendingChipLabel,
          ...(STATE_CHIP[pendingChipLabel.toLowerCase()] || chipTheme),
        },
        subtitle: p.subject ? `Subject: ${p.subject}` : undefined,
        date: null,
        detail,
      };
    }

    case "email_scheduled": {
      const p = prop?.payload || {};
      const stepKindLabel = STEP_TYPE_LABELS[p.step_type] || "Email";
      const themeKey = stepThemeKey(p.step_type);
      const isEmailStep = themeKey === "email";
      const rawStatus = (p.status || "scheduled").toString().toLowerCase();
      const chipLabel = SCHEDULED_STATUS_LABELS[rawStatus] || "Scheduled";
      const chipTheme = STATE_CHIP[chipLabel.toLowerCase()] || STATE_CHIP.default;
      // The right-side date column shows the scheduled run time. Prefer the
      // payload's `show_date_time` (pre-formatted by the backend, e.g.
      // "5/29/2026, 9:26:00 AM") so it matches what the backend considers
      // the official send window; fall back to the raw `scheduled_at` epoch.
      // Falls back to `created_at` only when neither is present.
      return {
        ...ICON_THEMES[themeKey],
        title: withStepPrefix(
          pickTitle(prop, `${stepKindLabel} Scheduled`),
          p.step_number,
        ),
        chip: { label: chipLabel, ...chipTheme },
        subtitle: p.subject ? `Subject: ${p.subject}` : undefined,
        date: p.show_date_time || p.scheduled_at || date,
        detail: isEmailStep ? (
          <EmailSentList
            contactDetails={contactDetails}
            emailData={adaptCampaignEmailSent(prop, contactDetails?.email, {
              status: "scheduled",
              processing_date_time: p.scheduled_at,
            })}
            showOperation={false}
          />
        ) : (
          renderStepDetailCard(prop, contactDetails, contactId)
        ),
      };
    }

    case "campaign_history": {
      const p = prop?.payload || {};
      const stateKey = (p.state || "").toLowerCase();
      const chipTheme = STATE_CHIP[stateKey] || STATE_CHIP.default;
      return {
        ...ICON_THEMES.campaign,
        title: pickTitle(
          prop,
          `Enrolled in campaign: ${p.campaign_name || "Campaign"}`,
        ),
        // `state` is frozen at enrolment. Once a step has actually sent, "Ready To Run"
        // is a lie sitting directly above a "Step 1: Email sent" row, so a started
        // campaign reads Active regardless of the snapshot.
        // The backend now resolves the campaign's CURRENT state at request time and sends it as
        // campaign_state_now. Prefer it over everything else: payload.state is frozen at enrolment
        // (a scheduled campaign read "Ready To Run") and the old "started -> Active" shortcut below
        // was a second lie on top, keeping a paused or completed campaign forever Active.
        chip: prop?.campaign_state_now
          ? {
              label: prop.campaign_state_now,
              ...(STATE_CHIP[prop.campaign_state_now.toLowerCase()] || STATE_CHIP.default),
            }
          : p.actual_started_at
            ? { label: "Active", ...(STATE_CHIP.active || STATE_CHIP.default) }
            : p.state
              ? { label: toTitleCase(p.state), ...chipTheme }
              : null,
        subtitle:
          typeof p.total_steps === "number"
            ? `Sequence Steps: ${p.total_steps} steps`
            : undefined,
        // Truth ladder for "Campaign started": the first real send to this contact, else
        // when the campaign was set to run, else when it was created. enrolled_at is LAST
        // because it is the moment the contact was added to CRM, which has nothing to do
        // with the campaign — that fallback is exactly what the tester reported.
        date:
          p.actual_started_at ||
          p.campaign_scheduled_date ||
          p.campaign_created_at ||
          p.enrolled_at ||
          date,
      };
    }

    case "sequence_history": {
      // Sales-sequence enrollment record. Subtitle is intentionally minimal -
      // just the step count - so the row reads as a clean marker. The
      // detailed schedule info (from-email, start date, timezone) is
      // available in the Emails tab once the queued touches surface there.
      const p = prop?.payload || {};
      // prop.status is the ACTIVITY row's status, written once at enrollment, so this pill said
      // Active for the rest of time - including on a sequence that had finished, been unenrolled or
      // been stopped by an unsubscribe. The backend now resolves the enrollment's current state at
      // request time and sends sequence_state_now; prefer it, exactly as the campaign row above
      // prefers campaign_state_now, and keep the frozen value only as a fallback for rows the
      // resolver could not reach.
      const stateKey = (prop?.sequence_state_now || prop?.status || p.state || "").toLowerCase();
      const chipTheme = STATE_CHIP[stateKey] || STATE_CHIP.default;
      return {
        ...ICON_THEMES.campaign,
        title: pickTitle(
          prop,
          `Enrolled in sequence: ${p.sequence_name || "Sales Sequence"}`,
        ),
        chip: prop?.sequence_state_now
          ? { label: prop.sequence_state_now, ...chipTheme }
          : prop?.status
            ? { label: toTitleCase(prop.status), ...chipTheme }
            : null,
        subtitle:
          typeof p.total_steps === "number"
            ? `${p.total_steps} steps`
            : undefined,
        date: p.enrolled_at || date,
      };
    }

    case "call_scheduled": {
      // Sales-sequence call task scheduled for a future date. Detail goes
      // through ListingTaskCard so the body matches the standalone Calls
      // tab (title strip + formatted notes) instead of a plain grey block.
      const p = prop?.payload || {};
      const rawStatus = (p.status || prop?.status || "scheduled")
        .toString()
        .toLowerCase();
      const chipTheme = STATE_CHIP[rawStatus] || STATE_CHIP.scheduled;
      return {
        ...ICON_THEMES.call,
        title: withStepPrefix(
          pickTitle(prop, "Call Scheduled"),
          p.step_number,
        ),
        chip: { label: SCHEDULED_STATUS_LABELS[rawStatus] || "Scheduled", ...chipTheme },
        subtitle: p.title || undefined,
        date: p.scheduled_at || date,
        detail: renderSalesSequenceDetailCard(
          prop,
          contactDetails,
          contactId,
          "call",
        ),
      };
    }

    case "task_scheduled": {
      // Sales-sequence to-do task scheduled for a future date. Same path
      // as call_scheduled above - renders the rich card via
      // ListingTaskCard instead of the plain grey notes block.
      const p = prop?.payload || {};
      const rawStatus = (p.status || prop?.status || "scheduled")
        .toString()
        .toLowerCase();
      const chipTheme = STATE_CHIP[rawStatus] || STATE_CHIP.scheduled;
      return {
        ...ICON_THEMES.task,
        title: withStepPrefix(
          pickTitle(prop, "Task Scheduled"),
          p.step_number,
        ),
        chip: { label: SCHEDULED_STATUS_LABELS[rawStatus] || "Scheduled", ...chipTheme },
        subtitle: p.title || undefined,
        date: p.scheduled_at || date,
        detail: renderSalesSequenceDetailCard(
          prop,
          contactDetails,
          contactId,
          "todo",
        ),
      };
    }

    case "call":
      // Right column shows the call's Due Date (show_time_stamp), not the
      // record's created_at - the user cares about when the call is/was
      // scheduled, not when the log entry was created.
      return {
        ...ICON_THEMES.call,
        title: pickTitle(prop, "Logged call"),
        subtitle: prop?.subject,
        date: prop?.show_time_stamp || date,
        detail: (
          <ListingTaskCard
            contactDetails={contactDetails}
            logData={pickTaskContent(prop)}
            contact_id={contactId}
            type={"call"}
            showOperation={false}
          />
        ),
      };

    case "todo":
      // Right column shows the task's Due Date via show_time_stamp - the
      // field the backend updates when the user picks a date in
      // ListingTaskCard. processing_date_time was added for emails and
      // doesn't reliably reflect task due-date edits.
      return {
        ...ICON_THEMES.task,
        title: pickTitle(prop, "Task"),
        subtitle: prop?.subject,
        date: prop?.show_time_stamp || date,
        detail: (
          <ListingTaskCard
            contactDetails={contactDetails}
            logData={pickTaskContent(prop)}
            contact_id={contactId}
            type={"todo"}
            showOperation={false}
          />
        ),
      };

    case "notes":
      return {
        ...ICON_THEMES.note,
        title: pickTitle(prop, "Note added"),
        date,
        detail: (
          <ListingNoteCard
            contactDetails={contactDetails}
            logData={
              prop?.is_compressed
                ? {
                    ...prop,
                    notes_body: decompressNotes(prop.notes_body),
                  }
                : prop
            }
            contact_id={contactId}
            showOperation={false}
          />
        ),
      };

    case "meetings":
      // Meetings carry the scheduled meeting time on
      // `event_startDateTime_timestamp` (epoch ms) or
      // `event_startDateTime` (ISO string). That's the date the user cares
      // about - not when the log entry was saved. Fall back to created_at
      // only when neither is present.
      return {
        ...ICON_THEMES.meeting,
        title: pickTitle(prop, "Meeting"),
        subtitle: prop?.event_title || prop?.title,
        date:
          prop?.event_startDateTime_timestamp ||
          prop?.event_startDateTime ||
          date,
        detail: (
          <ListingMeetingCard
            contactDetails={contactDetails}
            logData={
              prop?.is_compressed
                ? {
                    ...prop,
                    event_description: decompressNotes(prop.event_description),
                  }
                : prop
            }
            contactId={contactId}
            showOperation={false}
          />
        ),
      };

    case "email_unenrolled": {
      // Scheduled touch that was cancelled because the contact was unenrolled
      // from the campaign. Match the icon to the actual step kind (email /
      // task / call) and render with a grey "Unenrolled" chip so it visually
      // de-emphasises against active touches.
      const p = prop?.payload || {};
      const stepKindLabel = STEP_TYPE_LABELS[p.step_type] || "Email";
      const themeKey = stepThemeKey(p.step_type);
      const isEmailStep = themeKey === "email";
      // Right-side date column carries the scheduled date - subtitle keeps
      // just the subject for parity with other scheduled rows. Prefer the
      // backend's pre-formatted `show_date_time` over the raw epoch.
      return {
        ...ICON_THEMES[themeKey],
        title: withStepPrefix(
          pickTitle(prop, `${stepKindLabel} Unenrolled`),
          p.step_number,
        ),
        chip: { label: "Unenrolled", ...STATE_CHIP.unenrolled },
        subtitle: p.subject ? `Subject: ${p.subject}` : undefined,
        date: p.show_date_time || p.scheduled_at || date,
        detail: isEmailStep ? (
          <EmailSentList
            contactDetails={contactDetails}
            emailData={adaptCampaignEmailSent(prop, contactDetails?.email, {
              status: "unenrolled",
              processing_date_time: p.scheduled_at,
            })}
            showOperation={false}
          />
        ) : (
          renderStepDetailCard(prop, contactDetails, contactId)
        ),
      };
    }

    case "campaign_unenrolled": {
      // Marker activity for the unenrollment itself. Subtitle keeps a short
      // "Removed from N campaign(s)" summary - the per-touch cancellations
      // already surface as their own email_unenrolled rows just below, so
      // duplicating the count here would be noise.
      const p = prop?.payload || {};
      const removedCount =
        typeof p.deleted_cam_contacts === "number" && p.deleted_cam_contacts > 0
          ? p.deleted_cam_contacts
          : null;
      // Cancelled steps now live INSIDE this row (backend `cancelled_steps`).
      // They used to be one standalone row each, plus the original scheduled row
      // they contradicted - 14 rows for a 7-step unenroll.
      const cancelledSteps = Array.isArray(prop?.cancelled_steps)
        ? prop.cancelled_steps
        : [];
      const stepWord = cancelledStepsSummary(prop, cancelledSteps.length);
      return {
        ...ICON_THEMES.campaign,
        title: pickTitle(prop, "Campaign Unenrolled"),
        chip: { label: "Unenrolled", ...STATE_CHIP.unenrolled },
        subtitle: cancelledSteps.length
          ? stepWord
          : removedCount !== null
            ? `Removed from ${removedCount} campaign${removedCount === 1 ? "" : "s"}`
            : "Removed from campaign",
        date: p.unenrolled_at || date,
        detail: renderCancelledSteps(prop),
        detailToggleLabel: cancelledSteps.length
          ? `View ${stepWord}`
          : "View details",
      };
    }

    case "sequence_unenrolled": {
      // Marker activity written when a contact is removed from a sales
      // sequence. Mirrors campaign_unenrolled; the enrollment's own
      // sequence_history row also flips to an "Unenrolled" chip.
      const p = prop?.payload || {};
      const cancelled =
        typeof p.cancelled_scheduled_mails === "number" &&
        p.cancelled_scheduled_mails > 0
          ? p.cancelled_scheduled_mails
          : null;
      const cancelledSteps = Array.isArray(prop?.cancelled_steps)
        ? prop.cancelled_steps
        : [];
      const stepWord = cancelledStepsSummary(prop, cancelledSteps.length);
      return {
        ...ICON_THEMES.campaign,
        title: pickTitle(prop, "Sequence Unenrolled"),
        chip: { label: "Unenrolled", ...STATE_CHIP.unenrolled },
        subtitle: cancelledSteps.length
          ? stepWord
          : cancelled !== null
            ? `Cancelled ${cancelled} scheduled email${cancelled === 1 ? "" : "s"}`
            : "Removed from sequence",
        date: p.unenrolled_at || date,
        detail: renderCancelledSteps(prop),
        detailToggleLabel: cancelledSteps.length
          ? `View ${stepWord}`
          : "View details",
      };
    }

    case "attachments": {
      // Lightweight marker row - the backend payload only carries
      // who/when, not the file metadata itself. No expand panel because
      // there's nothing more to show than the activity_title and date.
      return {
        ...ICON_THEMES.attachment,
        title: pickTitle(prop, "Attachment uploaded"),
        date,
      };
    }

    default:
      return null;
  }
};

// ── Timeline ordering model ────────────────────────────────────────────────
// One rule per section instead of a direction that flips halfway down the feed.
//   Upcoming : things that have NOT happened and are due later — soonest first.
//   Activity : everything that already happened — newest first.
// Both are grouped under day headers so the date order is visible at a glance.
// The timeline feed is unpaginated (the backend returns every row in one call),
// so ordering here is safe: nothing can reorder across a page boundary.
const PENDING_STEP_ACTIVITY_TYPES = [
  "email_scheduled",
  "call_scheduled",
  "task_scheduled",
  "pending",
];

const asEpoch = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const asEpochLoose = (value) =>
  typeof value === "string" ? Date.parse(value) || 0 : asEpoch(value);

// The timestamp a row DISPLAYS. Mirrors buildRow's own date choices, except it
// prefers the numeric epoch fields over the pre-formatted `show_date_time`
// string: that string is date-only, so parsing it would collapse every send on
// a given day to midnight and scramble same-day ordering.
const rowDisplayTs = (prop) => {
  const p = prop?.payload || {};
  const base =
    asEpoch(prop?.created_at_ms) ||
    asEpoch(prop?.created_at) ||
    asEpochLoose(prop?.created_at);
  switch (prop?.activityType) {
    case "call":
    case "todo":
      return asEpoch(prop?.show_time_stamp) || base;
    case "meetings":
      return asEpoch(prop?.event_startDateTime_timestamp) || base;
    case "log":
    case "mail":
    case "sent":
    case "email":
    case "received":
      return asEpoch(prop?.processing_date_time) || base;
    case "email_sent":
      return asEpoch(p.sent_at) || asEpochLoose(p.show_date_time) || base;
    case "email_scheduled":
    case "call_scheduled":
    case "task_scheduled":
    case "pending":
    case "email_unenrolled":
      // A cancelled/parked step is dated by WHEN IT WAS CANCELLED. Its original future
      // due date would otherwise float a dead row to the top of the feed.
      return (
        asEpoch(prop?.suppressed_at) ||
        asEpoch(p.scheduled_at) ||
        asEpochLoose(p.show_date_time) ||
        base
      );
    case "campaign_history":
      return asEpoch(p.actual_started_at) || asEpoch(p.enrolled_at) || base;
    case "campaign_unenrolled":
    case "sequence_unenrolled":
      return asEpoch(p.unenrolled_at) || base;
    default:
      return base;
  }
};

// "Upcoming" means: it has not happened AND its own date is still in the future.
// A cancelled touch is never upcoming, however far out its original date was —
// that is the whole reason an unenrolled contact's timeline used to look wrong.
const isUpcomingRow = (prop, ts, now) => {
  if (!ts || ts <= now) return false;
  if (prop?.unenrolled === true) return false;
  const at = prop?.activityType;
  const status = String(prop?.status || "").toLowerCase();
  if (status === "unenrolled") return false;
  // Parked because the contact moved to a sales sequence: it will never happen, so it
  // belongs in Activity at the moment it was cancelled - not in Upcoming on its old
  // due date. This is what stopped the Emails tab and the timeline contradicting
  // each other on the same touch.
  if (status === "suppressed" || prop?.suppressed_by) return false;
  // The backend re-reads a campaign step's status when the timeline is requested and sets
  // this when the step can no longer happen (its campaign was deleted or has ended, or the
  // mail already went out). A dead touch is not work still to come, whatever date it carries
  // - listing it under Upcoming is how "step 2 and 3 still show as scheduled" happened.
  if (prop?.step_terminal) return false;
  if (PENDING_STEP_ACTIVITY_TYPES.includes(at)) return true;
  if (at === "mail" && status === "scheduled") return true;
  // An open manual call/task due later is genuinely upcoming work.
  if ((at === "call" || at === "todo") && !prop?.status) return true;
  if (at === "meetings") return true;
  return false;
};

const dayKeyOf = (ts) => {
  if (!ts) return "undated";
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
const dayLabelOf = (ts) => {
  if (!ts) return "No date";
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const STEP_TYPE_WORD = { 1: "Email", 2: "Task", 3: "Call" };

// Cancelled steps are collapsed by the backend onto their parent unenroll row.
// Each one expands on its own so a rep can read exactly what a cancelled touch
// would have said, without the seven separate (and contradictory) rows the feed
// used to carry per unenroll.
const CancelledStepsList = ({ steps }) => {
  const [openStep, setOpenStep] = useState(null);
  if (!Array.isArray(steps) || !steps.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {steps.map((s, i) => {
        const isOpen = openStep === i;
        const body = decompressAndFormat(s?.body, s?.is_compressed) || "";
        // Comments left on a cancelled step travel with it (the backend hangs them off the
        // step now that the step row itself is collapsed away). Without this they were
        // written, stored, and never shown again.
        const stepComments = Array.isArray(s?.timeline_comments)
          ? s.timeline_comments
          : [];
        const canExpand = !!body || stepComments.length > 0;
        return (
          <div
            key={`cs${i}`}
            style={{
              borderTop: i === 0 ? "none" : `1px solid ${BORDER.DEFAULT}`,
            }}
          >
            <div
              onClick={() => canExpand && setOpenStep(isOpen ? null : i)}
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "baseline",
                flexWrap: "wrap",
                padding: "7px 2px",
                fontSize: "12.5px",
                color: GREY.SECONDARY,
                cursor: canExpand ? "pointer" : "default",
              }}
            >
              {canExpand && (
                <IconComponent
                  iconLabel={
                    isOpen
                      ? ICON_LABELS.ACTION_ARROW
                      : ICON_LABELS.KEYBOARD_ARROW_RIGHT
                  }
                  color={GREY.TERTIARY}
                  fontSize={"0.75rem"}
                />
              )}
              <span style={{ fontWeight: 600, color: GREY.PRIMARY }}>
                {s?.step_number ? `Step ${s.step_number}` : "Step"}
              </span>
              <span>{STEP_TYPE_WORD[s?.step_type] || "Email"}</span>
              {s?.subject ? <span>· {s.subject}</span> : null}
              {s?.scheduled_at ? (
                <span style={{ color: GREY.TERTIARY }}>
                  · was due {dayLabelOf(asEpoch(s.scheduled_at))}
                </span>
              ) : null}
              {stepComments.length ? (
                <span style={{ color: GREY.TERTIARY }}>
                  ·{" "}
                  {stepComments.length === 1
                    ? "1 comment"
                    : `${stepComments.length} comments`}
                </span>
              ) : null}
            </div>
            {isOpen && (
              <div
                style={{
                  padding: "2px 2px 10px 22px",
                  fontSize: "13px",
                  color: GREY.SECONDARY,
                  lineHeight: 1.5,
                }}
              >
                {body ? parse(body) : null}
                {stepComments.map((c) => (
                  <div
                    key={c._id}
                    style={{
                      borderLeft: `2px solid ${BORDER.DEFAULT}`,
                      paddingLeft: "10px",
                      marginTop: "8px",
                    }}
                  >
                    <TypographyInline
                      label={`${c.author || "Unknown"} commented`}
                      size={"xs"}
                      fontWeight={fontWeight.SEMIBOLD}
                      color={GREY.SECONDARY}
                    />
                    <div
                      style={{
                        fontSize: "13px",
                        color: GREY.PRIMARY,
                        lineHeight: "20px",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {parse(String(c.comments || ""))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// "7 steps cancelled · 2 comments". The comment count belongs in the collapsed summary:
// those comments live inside the expandable list, so nothing else hints that they exist.
// What ended the steps, in the words a rep would use. The line used to say only "N steps
// cancelled", which says nothing about why a prospect stopped hearing from us - and hardcoding
// "ended after reply" would be wrong for the other two endings. The backend sends the reason it
// actually recorded; anything it could not determine falls back to the bare count.
const CANCEL_REASON_TEXT = {
  reply_received: "Cancelled — sequence ended after reply",
  unsubscribed: "Cancelled — prospect unsubscribed",
  unenrolled: "Cancelled — contact unenrolled",
};

const cancelledStepsSummary = (prop, count) => {
  const steps = `${count} step${count === 1 ? "" : "s"}`;
  const reason = CANCEL_REASON_TEXT[String(prop?.cancelled_steps_reason || "").toLowerCase()];
  const word = reason ? `${reason} · ${steps}` : `${steps} cancelled`;
  const comments = Number(prop?.cancelled_steps_comments_count) || 0;
  if (!comments) return word;
  return `${word} · ${comments} comment${comments === 1 ? "" : "s"}`;
};

const renderCancelledSteps = (prop) => {
  const steps = prop?.cancelled_steps;
  if (!Array.isArray(steps) || !steps.length) return null;
  return <CancelledStepsList steps={steps} />;
};

const ContactDetailTimelineScreen = ({
  contactId,
  contactDetails,
  getTimelineLogList = () => {},
  dataList = [],
  screenLoading = false,
  totalTimeline = 0,
  // Re-fetch the contact-info panel (active campaigns + sequences) after a
  // sales-sequence unenroll so the "Sales Sequence" source chip clears and the
  // enroll guard frees up.
  refreshContactInfo = () => {},
}) => {
  const navigate = useNavigate();
  // No-op scroll handler. Pagination is intentionally disabled here -
  // the parent fetches all timeline records in a single call and the
  // earlier scroll-driven page=N fetches were duplicating data.
  const onScrollApiHandler = () => {};

  // Sales-sequence unenroll confirm modal. Holds the row (with sequence_id /
  // sequence_name) the user clicked Unenroll on. Null means closed.
  const [seqUnenrollTarget, setSeqUnenrollTarget] = useState(null);
  const [seqUnenrolling, setSeqUnenrolling] = useState(false);

  // Ends the contact's enrollment in a sales sequence: the backend flips the
  // enrollment to 'unenrolled', cancels remaining scheduled sends, and flips the
  // sequence_history timeline row. We then refresh the timeline + contact-info.
  const handleSeqUnenrollConfirm = async () => {
    const payload = {
      sequence_id:
        seqUnenrollTarget?.payload?.sequence_id ||
        seqUnenrollTarget?.sequence_id ||
        null,
      contact_id:
        contactDetails?.primary_record_id ||
        contactDetails?.contact_id ||
        contactId,
    };
    if (!payload.sequence_id) {
      EventEmitter.emit(EMITTER_ERROR, "Missing sequence id");
      setSeqUnenrollTarget(null);
      return;
    }
    try {
      setSeqUnenrolling(true);
      const res = await postRequest(
        navigate,
        crmEndpoints.salesSequenceUnenroll,
        payload,
      );
      EventEmitter.emit(
        EMITTER_SUCCESS,
        res?.data?.message || "Contact unenrolled from sequence",
      );
      // Refresh the timeline (the enrollment row flips to "Unenrolled" + a
      // sequence_unenrolled marker appears) AND the contact-info panel so the
      // "Sales Sequence" chip clears and the contact can be enrolled elsewhere.
      getTimelineLogList();
      refreshContactInfo();
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Could not unenroll - try again.",
      );
    } finally {
      setSeqUnenrolling(false);
      setSeqUnenrollTarget(null);
    }
  };

  // Tracks whether the parent's loading flag has flipped on at least
  // once during this mount. Used to keep the skeleton visible during the
  // tiny gap between mount and `setTimelineScreenLoading(true)` so the
  // empty-state doesn't flash. Reset to false on each (re)mount via the
  // initial useState - tab unmount/remount resets it automatically.
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const prevScreenLoading = useRef(false);
  useEffect(() => {
    if (prevScreenLoading.current && !screenLoading) {
      setHasFetchedOnce(true);
    }
    prevScreenLoading.current = screenLoading;
  }, [screenLoading]);

  // Refetch the timeline every time this tab mounts so switching away
  // (e.g. to Calls) and coming back picks up new activity. The parent's
  // single-call pagination means one request returns everything; no
  // duplicate ?page=N follow-ups.
  useEffect(() => {
    if (Object.keys(contactDetails || {}).length) {
      getTimelineLogList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactDetails]);

  // The backend already returns the timeline records in the right order
  // (newest-first across mixed activity types). The previous client-side
  // sort picked different timestamp fields per record kind
  // (payload.scheduled_at vs created_at_ms vs created_at) and produced a
  // non-monotonic order - e.g. a manually logged meeting sliding above a
  // call that was logged 90 seconds later. Trust the backend ordering.
  const allActivities = React.useMemo(
    () => dataList || [],
    [dataList],
  );

  const renderRow = (logData, index, keyPrefix, hideDate = false) => {
    const cfg = buildRow({ prop: logData, contactDetails, contactId });
    if (!cfg) return null;
    // Source pill (Marketing Campaign / Sales Sequence / Manual). Per the
    // 2026-05-25 backend contract, `record_type_for_crm` is one of those
    // three exact Title Case strings. We accept lowercase too as a defensive
    // fallback in case any legacy row slipped through the backfill, but
    // always render the canonical Title Case label so the pill is uniform
    // across the timeline + Calls / Tasks / Emails / Meetings tabs.
    const rawRecordType = logData?.record_type_for_crm;
    const typeChip = (() => {
      if (logData?.activityType === "call" && logData?.call_source === "dialer") {
        return {
          label: "Sales Call",
          bg: SUCCESS.W_50,
          color: SUCCESS.W_700,
          border: SUCCESS.W_100,
        };
      }
      // No pill when `record_type_for_crm` is missing - cleaner than a
      // generic placeholder. (Earlier iteration rendered "Calendar" for
      // Google-synced rows; removed per UX preference.)
      if (!rawRecordType || typeof rawRecordType !== "string") return null;
      const normalized = rawRecordType.trim().toLowerCase();
      // Palette matches SourceTypeChip's CSS classes exactly so the
      // timeline pill and the in-card pill share one visual language.
      if (normalized === "sales sequence") {
        return {
          label: "Sales Sequence",
          bg: "#F3EEFE",
          color: "#6E3FE0",
          border: "#E5DBFB",
        };
      }
      if (normalized === "marketing campaign") {
        return {
          label: "Marketing Campaign",
          bg: "#E6F0FF",
          color: "#1F5AB8",
          border: "#CCDEFB",
        };
      }
      if (normalized === "manual") {
        return {
          label: "Manual",
          bg: "#EFEDFB",
          color: "#4F46B0",
          border: "#DBD7F3",
        };
      }
      // Surface any unknown future value rather than silently dropping it.
      return {
        label: rawRecordType,
        bg: "#EFEDFB",
        color: "#4F46B0",
        border: "#DBD7F3",
      };
    })();
    // Marketing Campaign SCHEDULED sends go out at a randomized future time,
    // so that time is misleading — hide it (keep the date). Once the mail is
    // actually SENT we show the real send time. (Same record-type
    // normalization as the pill above.)
    // Sales Sequence rows behave the same way: the queued send time is the
    // engine's intent, not a commitment, so no clock time is shown until the
    // mail/call/task actually happened.
    const isAutomatedRow =
      typeof rawRecordType === "string" &&
      ["marketing campaign", "sales sequence"].includes(
        rawRecordType.trim().toLowerCase(),
      );
    // Unenrolled rows carry the campaign's coarse "due day" (UTC midnight) as their
    // timestamp, which renders as 5:30 AM in IST — a placeholder, not a real clock
    // time. Hide the time on those too (keep the date).
    const hideTime =
      isAutomatedRow &&
      [
        "email_scheduled",
        "email_pending",
        "pending",
        "email_unenrolled",
        "call_unenrolled",
        "task_unenrolled",
        "sequence_unenrolled",
        "call_scheduled",
        "task_scheduled",
      ].includes(logData?.activityType);
    // Sales-sequence rows ("Enrolled in sequence: …") get an inline
    // Unenroll button on the title line. Only show when the enrollment
    // hasn't already ended - if the row's status reads unenrolled /
    // completed / paused we don't surface the action.
    const seqStatus = String(
      logData?.status || logData?.payload?.state || "",
    )
      .toLowerCase()
      .trim();
    const isUnenrollableSeq =
      logData?.activityType === "sequence_history" &&
      !["unenrolled", "completed", "stopped"].includes(seqStatus);
    // Unenroll intentionally NOT offered from the timeline (product request) — the timeline is
    // a read-only history. Unenrolling lives on the active-sequence panel / sequence surfaces.
    // `isUnenrollableSeq` is kept because it still documents the row state and the unenroll
    // modal wiring below is shared.
    void isUnenrollableSeq;
    const trailingAction = null;
    // Comments the backend attached to THIS record. Rendered inside the row rather
    // than as timeline entries of their own: a comment is part of the touch it was
    // left on, and as a separate row it landed in a different section from its parent
    // (a future touch sits in Upcoming while its comment is past history).
    const rowComments = Array.isArray(logData?.timeline_comments)
      ? logData.timeline_comments
      : [];
    const commentsBlock = rowComments.length ? (
      <div style={{ marginTop: cfg.detail ? "10px" : 0 }}>
        {rowComments.map((c) => (
          <div
            key={c._id}
            style={{
              borderLeft: `2px solid ${BORDER.DEFAULT}`,
              paddingLeft: "10px",
              marginTop: "8px",
            }}
          >
            <TypographyInline
              label={`${c.author || "Unknown"} commented`}
              size={"xs"}
              fontWeight={fontWeight.SEMIBOLD}
              color={GREY.SECONDARY}
            />
            <div
              style={{
                fontSize: "13px",
                color: GREY.PRIMARY,
                lineHeight: "20px",
                whiteSpace: "pre-wrap",
              }}
            >
              {parse(String(c.comments || ""))}
            </div>
          </div>
        ))}
      </div>
    ) : null;
    const detailWithComments =
      cfg.detail || commentsBlock ? (
        <>
          {cfg.detail}
          {commentsBlock}
        </>
      ) : null;
    return (
      <TimelineRow
        key={`${keyPrefix}-${logData?._id || index}`}
        iconLabel={cfg.icon}
        iconColor={cfg.color}
        iconBg={cfg.bg}
        iconBorder={cfg.border}
        title={cfg.title}
        chip={cfg.chip}
        typeChip={typeChip}
        subtitle={cfg.subtitle}
        date={cfg.date}
        detail={detailWithComments}
        detailToggleLabel={cfg.detailToggleLabel || "View details"}
        hideDate={hideDate}
        trailingAction={trailingAction}
        hideTime={hideTime}
      />
    );
  };

  return (
    <div
      className={`${classes.taskScreenMainContainer} allTimelineList`}
      id="comments_body"
      onScroll={onScrollApiHandler}
    >
      {(() => {
        // Show the skeleton while we either know we're loading OR we know
        // a fetch is imminent (contact loaded but data hasn't arrived yet).
        // Without the second branch, there's a one-frame flash of the
        // empty state between mount and `setTimelineScreenLoading(true)`.
        const isLoading =
          screenLoading ||
          (Object.keys(contactDetails || {}).length > 0 &&
            (!allActivities || allActivities.length === 0) &&
            !hasFetchedOnce);
        if (isLoading) {
          return (
            <SkeletonLoaderCRMScreens accordionLengths={5} buttonsLength={0} />
          );
        }
        if (allActivities.length) {
          // Split into Upcoming / Activity and give each ONE ordering rule.
          // Previously groups ran newest-first while the rows inside a group ran
          // oldest-first, so there was no direction the eye could follow.
          const now = Date.now();
          const decorated = allActivities.map((logData, idx) => {
            const ts = rowDisplayTs(logData);
            return {
              logData,
              idx,
              ts,
              upcoming: isUpcomingRow(logData, ts, now),
            };
          });
          const upcoming = decorated
            .filter((d) => d.upcoming)
            .sort((a, b) => a.ts - b.ts);
          const history = decorated
            .filter((d) => !d.upcoming)
            // Undated rows sink to the bottom instead of jumping to the top.
            .sort((a, b) => (b.ts || 0) - (a.ts || 0));

          const renderSection = (items, keyPrefix) => {
            // A day header only earns its place when it actually GROUPS rows. One header
            // per single row just repeated the date the row already prints on its right,
            // which made the list read as mostly empty scaffolding.
            const dayCounts = items.reduce((acc, it) => {
              const k = dayKeyOf(it.ts);
              acc[k] = (acc[k] || 0) + 1;
              return acc;
            }, {});
            let lastDay = null;
            return items.map((item) => {
              const day = dayKeyOf(item.ts);
              const showDay = day !== lastDay && dayCounts[day] > 1;
              lastDay = day;
              return (
                <React.Fragment key={`${keyPrefix}_${item.logData?._id || item.idx}`}>
                  {showDay && (
                    <div
                      style={{
                        // Opaque band: the list's vertical rail sits at x=23px with
                        // z-index 0, so without a solid background + a higher layer it
                        // ran straight through the header text.
                        position: "relative",
                        zIndex: 1,
                        background: "#ffffff",
                        padding: "10px 0 2px 0",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        color: GREY.SECONDARY,
                        textTransform: "uppercase",
                      }}
                    >
                      {dayLabelOf(item.ts)}
                    </div>
                  )}
                  {renderRow(item.logData, item.idx, keyPrefix, dayCounts[day] > 1)}
                </React.Fragment>
              );
            });
          };

          const sectionHeading = (label, count, extra) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                // No bottom border. A full-width divider here cut straight across the
                // vertical rail, which read as a stray line through the heading.
                // Opaque + layered for the same reason as the day header.
                position: "relative",
                zIndex: 1,
                background: "#ffffff",
                margin: extra ? "0 0 2px 0" : "14px 0 2px 0",
                padding: "4px 0 4px 0",
              }}
            >
              <span
                style={{ fontSize: "13px", fontWeight: 700, color: GREY.PRIMARY }}
              >
                {label}
              </span>
              <span
                style={{
                  fontSize: "11.5px",
                  fontWeight: 600,
                  color: GREY.SECONDARY,
                  background: BORDER.DEFAULT,
                  borderRadius: "999px",
                  padding: "1px 8px",
                }}
              >
                {count}
              </span>
            </div>
          );

          return (
            <div className={timelineStyles.timelineList}>
              {upcoming.length > 0 && (
                <>
                  {sectionHeading("Upcoming", upcoming.length, true)}
                  {/* railGroup draws the connector from this section's first icon to
                      its last, so it never dangles into the gap below. */}
                  <div className={timelineStyles.railGroup}>
                    {renderSection(upcoming, "up")}
                  </div>
                </>
              )}
              {history.length > 0 && (
                <>
                  {upcoming.length > 0
                    ? sectionHeading("Activity", history.length)
                    : null}
                  <div className={timelineStyles.railGroup}>
                    {renderSection(history, "tl")}
                  </div>
                </>
              )}
            </div>
          );
        }
        return (
          <div className={classes.TaskHead}>
            <ContactDetailEmptyScreen
              src={TIMELINE_CRM}
              label={STRINGS.TIMELINE_NOTE}
            />
          </div>
        );
      })()}

      {/* Sales-sequence unenroll confirm modal */}
      {seqUnenrollTarget && (
        <CustomModal
          variant="error"
          open={true}
          close={() => {
            if (seqUnenrolling) return;
            setSeqUnenrollTarget(null);
          }}
          header={{
            heading: { id: "seq_unenroll", label: "Unenroll" },
          }}
          footer={{
            right: (
              <div style={{ display: "flex", gap: "12px" }}>
                <Button
                  label="Cancel"
                  variant="ghost"
                  size="sm36"
                  disabled={seqUnenrolling}
                  onClick={() => setSeqUnenrollTarget(null)}
                />
                <Button
                  label="Unenroll"
                  size="sm36"
                  variant="primary"
                  isLoading={seqUnenrolling}
                  onClick={handleSeqUnenrollConfirm}
                />
              </div>
            ),
          }}
        >
          <TypographyInline
            label={`Are you sure you want to unenroll this contact from "${
              seqUnenrollTarget?.payload?.sequence_name || "this sequence"
            }"?`}
            size="sm"
            fontWeight={fontWeight.REGULAR}
            color={GREY.SECONDARY}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default ContactDetailTimelineScreen;
